import React, { useState, useMemo } from 'react';
import { 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Grid, 
  Check, 
  X, 
  Loader2 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getFieldOptionsSync, isSelectField as checkIsSelectField } from '../../hooks/useFieldOptions';

/**
 * SpreadsheetView - Reusable spreadsheet view component for entities
 * Provides Excel-like interface with inline editing, sorting, and clipboard export
 */
const SpreadsheetView = ({
  entityName,
  entityConfig,
  data = [],
  columns = [],
  loading = false,
  onView,
  onEdit,
  onDelete,
  onCellSave,
  onSelect,
  selectedItems = new Set(),
  selectAll = false,
  onSelectAll,
  refs = {},
  getDisplayValue,
  isFieldEditable,
  sortConfig,
  onSort,
  i18n
}) => {
  const [editingCell, setEditingCell] = useState(null); // { itemId, field }
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);

  // Helper to get sortable value for a field
  const getSortableValue = (item, columnKey, fieldConfig) => {
    const rawValue = item[columnKey];
    
    // Handle numeric fields
    if (fieldConfig?.dataType === 'integer' || fieldConfig?.dataType === 'number' || fieldConfig?.dataType === 'decimal') {
      return Number(rawValue) || 0;
    }
    
    // Handle date fields
    if (fieldConfig?.dataType === 'date' || fieldConfig?.dataType === 'datetime') {
      return rawValue ? new Date(rawValue).getTime() : 0;
    }
    
    // Handle boolean fields
    if (fieldConfig?.dataType === 'boolean') {
      return rawValue ? 1 : 0;
    }
    
    // Default: string sorting (case-insensitive)
    return String(rawValue || '').toLowerCase();
  };

  // Sort data
  const getSortedData = () => {
    if (!sortConfig.key) return data;

    const column = columns.find(col => col.key === sortConfig.key);
    const fieldConfig = column?.fieldConfig;
    const isNumeric = fieldConfig?.dataType === 'integer' || 
                      fieldConfig?.dataType === 'number' || 
                      fieldConfig?.dataType === 'decimal';

    return [...data].sort((a, b) => {
      const aVal = getSortableValue(a, sortConfig.key, fieldConfig);
      const bVal = getSortableValue(b, sortConfig.key, fieldConfig);
      
      // Handle numeric/date sorting
      if (isNumeric || fieldConfig?.dataType === 'date' || fieldConfig?.dataType === 'datetime') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string sorting
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Copy all data to clipboard
  const handleCopyToClipboard = () => {
    const sorted = getSortedData();
    
    // Build headers
    const headers = columns.map(col => col.label);
    
    // Build rows
    const rows = sorted.map(item => 
      columns.map(col => {
        if (getDisplayValue) {
          return getDisplayValue(item, col.key, col.fieldConfig);
        }
        return item[col.key] || '-';
      })
    );

    const tsvContent = [headers, ...rows]
      .map(row => row.join('\t'))
      .join('\n');

    navigator.clipboard.writeText(tsvContent).then(() => {
      toast.success(`${entityName} data copied to clipboard! You can now paste it into Excel or Google Sheets.`, {
        duration: 4000,
        icon: '📋'
      });
    }).catch(err => {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  // Handle cell edit
  const handleCellEdit = (itemId, field, value) => {
    setEditingCell({ itemId, field });
    setEditingValue(value || '');
  };

  // Save cell edit
  const handleSaveCellEdit = async () => {
    if (!editingCell || !onCellSave) {
      setEditingCell(null);
      return;
    }

    const { itemId, field } = editingCell;
    // Find item by matching its primary key (with type coercion for safety)
    const pk = entityConfig?.entity?.primaryKey || 'id';
    const item = data.find(d => {
      const dId = d[pk];
      // Allow comparison between numbers and strings
      return dId === itemId || String(dId) === String(itemId) || Number(dId) === Number(itemId);
    });
    
    if (!item) {
      setEditingCell(null);
      return;
    }

    // If value hasn't changed, just cancel
    if (item[field] === editingValue) {
      setEditingCell(null);
      setEditingValue('');
      return;
    }

    try {
      setSavingCell(true);
      await onCellSave(itemId, field, editingValue);
      setEditingCell(null);
      setEditingValue('');
    } catch (err) {
      console.error('Failed to save cell:', err);
      toast.error('Failed to update: ' + err.message);
    } finally {
      setSavingCell(false);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  // Get select field label
  const getSelectFieldLabel = (fieldName, value) => {
    if (!value || !entityConfig) return value || '-';
    
    const fieldOptions = getFieldOptionsSync(entityConfig, fieldName);
    if (fieldOptions.length === 0) return value;
    
    const option = fieldOptions.find(opt => opt.value === value);
    return option ? (option.label || option.value) : value;
  };

  const sortedData = getSortedData();
  const primaryKey = entityConfig?.entity?.primaryKey || 'id';
  
  // Helper function to get a robust numeric ID from an item
  const getItemId = (item) => {
    let itemId = primaryKey ? item[primaryKey] : undefined;
    
    // Fallback: check for common ID patterns if primaryKey didn't work
    if (itemId === undefined || itemId === null) {
      const entityIdField = entityName ? `${entityName.charAt(0).toUpperCase() + entityName.slice(1)}ID` : null;
      itemId = item[entityIdField] || item.id || item.ID || item[Object.keys(item).find(k => k.toLowerCase().endsWith('id'))];
    }
    
    // Ensure itemId is a number for consistent comparison
    if (typeof itemId === 'number') return itemId;
    if (typeof itemId === 'string' && !isNaN(parseInt(itemId, 10))) return parseInt(itemId, 10);
    return itemId;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        {/* Header with instructions */}
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Grid className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Spreadsheet View - Double-click rows to view, click headers to sort, double-click cells to edit
              </span>
            </div>
            <button
              onClick={handleCopyToClipboard}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium flex items-center space-x-1"
            >
              <Download className="h-4 w-4" />
              <span>Copy All Data</span>
            </button>
          </div>
        </div>
        
        {/* Table */}
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              {/* Selection checkbox column */}
              {onSelect && (
                <th className="w-12 px-4 py-3 text-left border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={onSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
              )}
              
              {/* Dynamic columns */}
              {columns.map(column => (
                <th
                  key={column.key}
                  onClick={() => onSort && onSort(column.key)}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-200 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700"
                  style={{ minWidth: column.width }}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {sortConfig.key === column.key && (
                      <span className="text-blue-600 dark:text-blue-400 font-bold">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                    {column.editable && (
                      <Edit className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                </th>
              ))}
              
              {/* Actions column */}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-200 uppercase tracking-wider w-32 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          
          <tbody className="bg-white dark:bg-gray-800">
            {sortedData.map((item, rowIndex) => {
              const itemId = getItemId(item) ?? rowIndex;
              const isSelected = selectedItems.has(itemId);
              
              return (
                <tr
                  key={itemId}
                  className={`transition-colors ${
                    isSelected 
                      ? 'bg-blue-50 dark:bg-blue-900/30' 
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  ref={el => refs[itemId] = el}
                >
                  {/* Selection checkbox */}
                  {onSelect && (
                    <td className="px-4 py-3 border border-gray-300 dark:border-gray-600">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onSelect(itemId)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                  )}
                  
                  {/* Dynamic cells */}
                  {columns.map(column => {
                    const value = getDisplayValue 
                      ? getDisplayValue(item, column.key, column.fieldConfig)
                      : (item[column.key] || '-');
                    
                    const editableField = isFieldEditable 
                      ? isFieldEditable(item, column.key, column.fieldConfig)
                      : (column.editable ? column.key : null);
                    
                    const isEditing = editingCell?.itemId === itemId && 
                                     editingCell?.field === editableField;
                    
                    // Check if field is a select field
                    const isFieldSelect = editableField && entityConfig ? 
                      checkIsSelectField(entityConfig, editableField) : false;
                    const fieldOptions = isFieldSelect && entityConfig ? 
                      getFieldOptionsSync(entityConfig, editableField) : [];
                    
                    return (
                      <td
                        key={column.key}
                        className={`px-4 py-3 text-sm text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 ${
                          column.editable ? 'cursor-pointer hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : ''
                        }`}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (column.editable && editableField) {
                            handleCellEdit(itemId, editableField, item[editableField]);
                          }
                        }}
                        title={column.editable ? 'Double-click to edit' : ''}
                        style={{ minWidth: column.width }}
                      >
                        {isEditing ? (
                          <div className="flex items-center space-x-2">
                            {isFieldSelect && fieldOptions.length > 0 ? (
                              /* SELECT dropdown */
                              <select
                                value={editingValue || ''}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !savingCell) handleSaveCellEdit();
                                  if (e.key === 'Escape' && !savingCell) handleCancelEdit();
                                }}
                                disabled={savingCell}
                                className="w-full px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                autoFocus
                              >
                                <option value="">-- Select --</option>
                                {fieldOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label || option.value}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              /* TEXT input */
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !savingCell) handleSaveCellEdit();
                                  if (e.key === 'Escape' && !savingCell) handleCancelEdit();
                                }}
                                disabled={savingCell}
                                className="w-full px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                autoFocus
                              />
                            )}
                            {savingCell ? (
                              <Loader2 className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-spin" />
                            ) : (
                              <>
                                <button
                                  onClick={handleSaveCellEdit}
                                  className="text-green-600 hover:text-green-800 dark:text-green-400"
                                  title="Save (Enter)"
                                >
                                  <Check className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400"
                                  title="Cancel (Esc)"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-900 dark:text-gray-100">
                              {value}
                            </span>
                            {column.editable && (
                              <Edit className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100" />
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  
                  {/* Actions */}
                  <td className="px-4 py-3 text-sm border border-gray-300 dark:border-gray-600">
                    <div className="flex items-center space-x-2 justify-center">
                      {onView && (
                        <button
                          onClick={() => onView(item)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(itemId)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Empty state */}
        {sortedData.length === 0 && !loading && (
          <div className="text-center py-12">
            <Grid className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No data found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new {entityName}
            </p>
          </div>
        )}
      </div>
      
      {/* Instructions */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
        <div className="flex items-start space-x-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="space-y-1">
            <p><strong>Quick Tips:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Double-click any row to view details</li>
              <li>Click column headers to sort data</li>
              <li>Double-click editable cells (marked with <Edit className="inline h-3 w-3" />) to edit</li>
              <li>Click "Copy All Data" to copy the entire table</li>
              <li>Paste directly into Excel, Google Sheets, or any spreadsheet application</li>
              {onSelect && <li>Use checkboxes to select multiple items for bulk operations</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetView;

